import { AuthErrorType, type StandardAuthError } from '@memberjunction/ng-auth-services';

/**
 * Which identity the MJ client caches on this origin belong to.
 *
 * MJ 5.51 keys its persisted metadata snapshot (IndexedDB `MJ_Metadata`) by API URL only
 * (graphql-dataprovider `LocalStoragePrefix`), and the snapshot carries `CurrentUser`
 * (@memberjunction/core dist/generic/providerBase.js:34 MetadataFromSimpleObject). On a warm start
 * the provider serves that snapshot and skips GetAllMetadata — the only place CurrentUser is
 * fetched (providerBase.js:2920-2931 vs :3192) — so a browser that switches identity without
 * logging out runs the client as the PREVIOUS user (upstream defect #15, stage evidence
 * docs/FeedbackPlatform/testing_results/2026-09-30-oep-v2-stage-deployed/s1-D1-identity-mix-root-cause.txt).
 * MJ's logout clears those caches (MJAuthBase.clearClientCaches); nothing clears them when a new
 * identity signs in.
 *
 * Why a side marker rather than the snapshot's own CurrentUser: MJ has no supported read path for
 * the snapshot before setup. The keys are private statics (`ProviderBase.localStorageRootKey`,
 * `localStorageAllMetadataKey`, `localStorageFormatKey` — core providerBase.d.ts:1478-1481), the
 * loader is protected (`LoadLocalMetadataFromStorage`, :1477) and needs a configured provider, and
 * the stored value is a gzip/base64 blob whose format is an implementation detail. So EVERY
 * provider that can start a session in Explorer (magic link, ForgeRock) reconciles this marker on
 * sign-in; a marker written by one provider is therefore never stale for another.
 */
export const MJ_CACHE_IDENTITY_KEY = 'agu-mj-cache-identity';

/** MJ's client-cache IndexedDB (the snapshot plus mj:RunViewCache / RunQueryCache / DatasetCache stores). */
export const MJ_METADATA_DATABASE = 'MJ_Metadata';

/** How long a blocked delete may wait for other tabs to close their connection before failing closed. */
export const MJ_CACHE_DELETE_TIMEOUT_MS = 10_000;

/** Shown on the login screen when the caches could not be cleared; the button there signs out. */
export const MJ_CACHE_IDENTITY_USER_MESSAGE =
    'This browser still holds data from a different sign-in, and it could not be cleared. Close other tabs of this site and reload, or press Log in to sign out here.';

/** The caches belong to someone else and could not be cleared — the session must not start. */
export class MJCacheIdentityError extends Error {
    constructor(message: string, readonly Cause: unknown) {
        super(message);
        this.name = 'MJCacheIdentityError';
    }
}

/** What a provider hands the reconcile step. */
export interface MJCacheIdentityDeps {
    Storage: Storage;
    /** MJ's own logout clean slate (`MJAuthBase.clearClientCaches`). Resolves even when the IndexedDB delete is blocked. */
    ClearClientCaches(): Promise<void>;
    /** Resolves only once the database is actually gone. */
    DeleteDatabase(name: string): Promise<void>;
}

/** Normalizes an identity (email) for comparison; empty → null. */
export function NormalizeCacheIdentity(identity: string | null | undefined): string | null {
    const trimmed = (identity ?? '').trim().toLowerCase();
    return trimmed.length > 0 ? trimmed : null;
}

/**
 * Deletes an IndexedDB database and resolves only when the delete has completed. `onblocked` is not
 * completion: another tab still holds a connection (MJ's own cache closes on `versionchange`, so
 * this normally clears within moments). Rejects on error or when still blocked after `timeoutMs`.
 */
export function DeleteIndexedDatabase(name: string, timeoutMs: number = MJ_CACHE_DELETE_TIMEOUT_MS, idb: IDBFactory = indexedDB): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        let settled = false;
        let blocked = false;
        const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error(`IndexedDB "${name}" delete did not complete within ${timeoutMs} ms${blocked ? ' (blocked by another open tab)' : ''}`));
        }, timeoutMs);
        const request = idb.deleteDatabase(name);
        request.onsuccess = (): void => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve();
        };
        request.onerror = (): void => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            reject(request.error ?? new Error(`IndexedDB "${name}" delete failed`));
        };
        request.onblocked = (): void => {
            blocked = true; // keep waiting for onsuccess
        };
    });
}

/**
 * Makes the MJ client caches belong to `identity` before Explorer's setup reads them. Returns true
 * when it cleared, false when they already belonged to `identity`.
 *
 * Clears when the recorded owner differs or is unknown (no marker, or no identity to compare). The
 * marker is removed BEFORE clearing and written only AFTER the IndexedDB delete has completed, so
 * an interrupted clear leaves "unknown" and the next sign-in clears again. Any failure throws
 * MJCacheIdentityError: the caller must not start the session (fail closed).
 */
export async function ReconcileMJCacheIdentity(identity: string | null | undefined, deps: MJCacheIdentityDeps): Promise<boolean> {
    const owner = NormalizeCacheIdentity(identity);
    try {
        if (owner !== null && deps.Storage.getItem(MJ_CACHE_IDENTITY_KEY) === owner) return false;
        deps.Storage.removeItem(MJ_CACHE_IDENTITY_KEY);
        await deps.ClearClientCaches();
        await deps.DeleteDatabase(MJ_METADATA_DATABASE);
        if (owner !== null) deps.Storage.setItem(MJ_CACHE_IDENTITY_KEY, owner);
        return true;
    } catch (error) {
        throw new MJCacheIdentityError(`Could not clear the MJ client caches for the signed-in identity: ${error instanceof Error ? error.message : String(error)}`, error);
    }
}

/** The login-screen classification for a cache-identity failure; null for any other error. */
export function ClassifyMJCacheIdentityError(error: unknown): StandardAuthError | null {
    if (!(error instanceof MJCacheIdentityError)) return null;
    return { type: AuthErrorType.UNKNOWN_ERROR, message: error.message, originalError: error, userMessage: MJ_CACHE_IDENTITY_USER_MESSAGE };
}
